import React, { ComponentProps, useState } from 'react';
import { asFetchableBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { Heading } from '../../_lib/components/Heading/Heading';
import { Icons } from '../../components/atoms/SVG';
import ModalDialog from '../../components/organisms/ModalDialog/ModalDialog';
import AdditionalReadRestrictionsForm, {
  AdditionalReadRestrictionsFormTypes,
} from './partials/AdditionalReadRestrictionsForm';
import { SonForm } from '../../_lib/Form2';
import { ISonURL } from '../../types/IUrl';
import { InheritedRestrictionsList } from './partials/InheritedRestrictionList';
import { BlockBar, BlockBarButton } from '../../_lib/components/BlockBar';
import { AdditionalPrivilegesRestrictionsList } from './partials/AdditionalPrivilegesRestrictionsList';
import { PrivilegeType } from '../PrivilegeConfig/PrivilegeConfig.types';

type InheritedReadRestrictions = {
  pageLink: ISonURL;
  groupNames: string[];
};

type PageReadRestrictionsProps = {
  inheritedRestrictions: InheritedReadRestrictions[];
  additionalPrivilegesRestrictions?: PrivilegeType[];
  restrictedUserGroups: string[];
  form?: SonForm<AdditionalReadRestrictionsFormTypes>;
};

function PageReadRestrictions({
  inheritedRestrictions,
  additionalPrivilegesRestrictions,
  restrictedUserGroups,
  form,
}: PageReadRestrictionsProps) {
  const { t } = useSonTranslation('PageReadRestrictions');
  const [showAdditionalRestrictionsModal, setShowAdditionalRestrictionsModal] = useState(false);

  return (
    <div className="page-read-restrictions">
      <div className="page-read-restrictions__block">
        <Heading className="page-read-restrictions__heading" hierarchy={4}>
          {t('inheritedRestrictions.heading')}
        </Heading>
        {inheritedRestrictions && inheritedRestrictions.length > 0 ? (
          <InheritedRestrictionsList restrictions={inheritedRestrictions} />
        ) : (
          <p>{t('inheritedRestrictions.none')}</p>
        )}
      </div>

      <div className="page-read-restrictions__block">
        <Heading className="page-read-restrictions__heading" hierarchy={4}>
          {t('additionalPrivilegesRestrictions.heading')}
        </Heading>
        {additionalPrivilegesRestrictions && additionalPrivilegesRestrictions.length > 0 ? (
          <AdditionalPrivilegesRestrictionsList additionalPrivilegesRestrictions={additionalPrivilegesRestrictions} />
        ) : (
          <p>{t('additionalPrivilegesRestrictions.none')}</p>
        )}
      </div>

      <BlockBar heading={t('additionalRestrictions.heading')} hierarchy={4}>
        {form ? (
          <BlockBarButton
            text={t('additionalRestrictions.modalTitle')}
            svgSpriteId={Icons.EDIT_PENCIL}
            onClick={() => setShowAdditionalRestrictionsModal(true)}
          />
        ) : null}
      </BlockBar>
      <div className="page-read-restrictions__block page-read-restrictions__block--no-top-padding">
        {restrictedUserGroups && restrictedUserGroups.length > 0 ? (
          <>
            <p>
              <dfn className="page-read-restrictions__dfn">{t('inheritedRestrictions.visibleBy')}:</dfn>
              {restrictedUserGroups.length === 1 ? (
                <span className="page-read-restrictions__inherited-restrictions-group">
                  &nbsp;{restrictedUserGroups[0]}
                </span>
              ) : null}
            </p>
            {restrictedUserGroups.length > 1 ? (
              <ul className="page-read-restrictions__inherited-restrictions-ul">
                {restrictedUserGroups.map(group => (
                  <li className="page-read-restrictions__inherited-restrictions-li" key={group}>
                    {group}
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        ) : null}
      </div>
      {showAdditionalRestrictionsModal && form ? (
        <ModalDialog
          closeModal={() => setShowAdditionalRestrictionsModal(false)}
          headline={t('additionalRestrictions.modalTitle')}
          initialFocusIndex={1}
        >
          <AdditionalReadRestrictionsForm form={form} />
        </ModalDialog>
      ) : null}
    </div>
  );
}

export const PageReadRestrictionsBlock = asFetchableBlock(PageReadRestrictions, BlockType.pagereadrestrictions);
export type PageReadRestrictionsBlock = ComponentProps<typeof PageReadRestrictionsBlock>;
